import classNames from 'classnames';
import React, { useState } from 'react';
import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import getUserNames from '../../user/getUserNames';
import { useGetUser } from '../../user/useAllUsers';
import PostIcon from '../PostIcon';
import formatHistoryDate from './formatHistoryDate';
import * as styles from './HistoryRow.module.scss';
import useRestoreVersion from './useRestoreVersion';

const HISTORY_ROW_AUTHORS_FRAGMENT = gql(`
  fragment HistoryRow_authors on User {
    id
    ...useGetUser_user
  }
`);

interface HistoryRowProp {
  active: boolean;
  authors?: DocumentType<typeof HISTORY_ROW_AUTHORS_FRAGMENT>[] | null;
  postId: string;
  versionFrom: number;
  versionTo: number;
  editedAt: string;
  isCurrent: boolean;
  canRestore: boolean;
  onClick: (e: React.MouseEvent) => void;
}

const HistoryRow = ({
  active,
  authors,
  postId,
  editedAt,
  versionFrom,
  versionTo,
  isCurrent,
  canRestore,
  onClick,
}: HistoryRowProp) => {
  const getUser = useGetUser();
  const fullAuthors = authors?.map(getUser) ?? [];
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const restore = useRestoreVersion();

  return (
    <div
      className={classNames(styles.container, {
        [styles.isMenuOpen]: isMenuOpen,
      })}
    >
      <Button
        className={classNames(styles.button, { [styles.active]: active })}
        onClick={onClick}
      >
        {fullAuthors?.[0] ? (
          <Avatar user={fullAuthors[0]} />
        ) : (
          <div className={styles.postIconWrapper}>
            <PostIcon />
          </div>
        )}
        <div className={styles.description}>
          {fullAuthors && (
            <div className={styles.authors}>
              {getUserNames(fullAuthors, { limit: 2 })}
            </div>
          )}
          {isCurrent ? (
            'Currently editing'
          ) : (
            <div className={styles.timestamp}>
              {formatHistoryDate(editedAt)}
            </div>
          )}
        </div>
      </Button>
      <div className={styles.menuContainer}>
        {canRestore && (
          <OverflowMenu
            applyNegativeMargin
            iconSize="regular"
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
          >
            <MenuItem
              onClick={() => {
                restore({
                  postId,
                  versionFrom,
                  versionTo,
                });
              }}
            >
              Restore this version
            </MenuItem>
          </OverflowMenu>
        )}
      </div>
    </div>
  );
};

export default HistoryRow;
